import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoIcon,
  Sync as SyncIcon,
  Schedule as ScheduleIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { TableSortLabel } from '@mui/material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';

const SYNC_INTERVAL_OPTIONS = [
  { label: '1분', value: 60 },
  { label: '5분', value: 300 },
  { label: '10분', value: 600 },
  { label: '30분', value: 1800 },
  { label: '1시간', value: 3600 },
];

const AUTO_SYNC_STORAGE_KEY = 'portForwarding_autoSync';

// 정렬 가능한 컬럼 정의
const SORTABLE_COLUMNS = [
  { id: 'vm_name', label: 'VM 이름', numeric: false },
  { id: 'protocol', label: '프로토콜', numeric: false },
  { id: 'host_port', label: '호스트 포트', numeric: true },
  { id: 'vm_ip', label: 'VM IP', numeric: false },
  { id: 'vm_port', label: 'VM 포트', numeric: true },
  { id: 'description', label: '설명', numeric: false },
];

// 가져오기 미리보기 상태 표시 정보
const IMPORT_STATUS_INFO = {
  ready: { label: '추가', color: 'success' },
  pending_ip: { label: 'IP 대기', color: 'info' },
  already_exists: { label: '설정됨', color: 'default' },
  duplicate: { label: '중복', color: 'warning' },
  port_conflict: { label: '포트 충돌', color: 'warning' },
  vm_missing: { label: 'VM 없음', color: 'error' },
  vm_not_nat: { label: 'NAT 아님', color: 'error' },
  invalid: { label: '형식 오류', color: 'error' },
};

// 가져오기 실행 결과 표시 정보
const IMPORT_RESULT_INFO = {
  added: { label: '추가됨', color: 'success' },
  updated: { label: '교체됨', color: 'success' },
  stored: { label: '저장됨', color: 'info' },
  skipped: { label: '건너뜀', color: 'default' },
  failed: { label: '실패', color: 'error' },
};

const IMPORT_SAMPLE = 'MyDevVM\tTCP\t10004\t192.168.111.3\t22\tSSH';

// 안전한 비교 함수 (undefined/null은 가장 뒤로)
const compareValues = (a, b, orderBy) => {
  const av = a?.[orderBy];
  const bv = b?.[orderBy];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  return String(av).localeCompare(String(bv), undefined, { numeric: true });
};

function PortForwardingPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [usedPorts, setUsedPorts] = useState([]);
  const [portConfig, setPortConfig] = useState({ min_port: 10000, max_port: 65535 });
  const [vms, setVms] = useState([]);

  // 정렬 상태 (기본: VM 이름 ASC → 호스트 포트 ASC)
  const [orderBy, setOrderBy] = useState('vm_name');
  const [order, setOrder] = useState('asc');

  // Dialog states
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [autoMode, setAutoMode] = useState(false);
  const [autoSyncDialogOpen, setAutoSyncDialogOpen] = useState(false);

  // 가져오기(import) 상태
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importFileName, setImportFileName] = useState('');
  const [importOptions, setImportOptions] = useState({
    on_duplicate: 'skip',
    on_port_conflict: 'skip',
  });
  const [importPreview, setImportPreview] = useState(null);
  const [importApplied, setImportApplied] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  // Auto sync states (persisted in localStorage)
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(AUTO_SYNC_STORAGE_KEY) || '{}');
      return saved.enabled || false;
    } catch { return false; }
  });
  const [autoSyncInterval, setAutoSyncInterval] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(AUTO_SYNC_STORAGE_KEY) || '{}');
      return saved.interval || 300;
    } catch { return 300; }
  });
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const autoSyncTimerRef = useRef(null);

  // Form states
  const [newPortConfig, setNewPortConfig] = useState({ min_port: 10000, max_port: 65535 });
  const [newRule, setNewRule] = useState({
    vm_name: '',
    protocol: 'tcp',
    host_port: '',
    vm_port: '',
    description: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPortConfig(),
        loadAllRules(),
        loadUsedPorts(),
        loadVMs(),
      ]);
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPortConfig = async () => {
    try {
      const response = await apiService.getPortForwardingConfig();
      setPortConfig(response.data);
      setNewPortConfig(response.data);
    } catch (error) {
      console.error('포트 범위 설정 로드 실패:', error);
      enqueueSnackbar('포트 범위 설정 로드 실패', { variant: 'error' });
    }
  };

  const loadAllRules = async () => {
    try {
      const response = await apiService.getAllPortForwardingRules();
      setRules(response.data.rules || []);
    } catch (error) {
      console.error('포트 포워딩 규칙 로드 실패:', error);
      enqueueSnackbar('포트 포워딩 규칙 로드 실패', { variant: 'error' });
    }
  };

  const loadUsedPorts = async () => {
    try {
      const response = await apiService.getUsedPorts();
      setUsedPorts(response.data.used_ports || []);
    } catch (error) {
      console.error('사용 중인 포트 로드 실패:', error);
      enqueueSnackbar('사용 중인 포트 로드 실패', { variant: 'error' });
    }
  };

  const loadVMs = async () => {
    try {
      const response = await apiService.getVMs();
      setVms(response.data.vms || []);
    } catch (error) {
      console.error('VM 목록 로드 실패:', error);
    }
  };

  // 자동 동기화: 백그라운드에서 조용히 실행 (로딩 표시 없음)
  const silentSyncIP = useCallback(async () => {
    if (rules.length === 0) return;
    try {
      await apiService.syncAllPortForwardingIP();
      setLastSyncTime(new Date());
      await loadAllRules();
    } catch (error) {
      console.warn('자동 IP 동기화 실패:', error);
    }
  }, [rules.length]);

  // 자동 동기화 타이머 관리
  useEffect(() => {
    if (autoSyncTimerRef.current) {
      clearInterval(autoSyncTimerRef.current);
      autoSyncTimerRef.current = null;
    }
    if (autoSyncEnabled) {
      autoSyncTimerRef.current = setInterval(silentSyncIP, autoSyncInterval * 1000);
    }
    return () => {
      if (autoSyncTimerRef.current) {
        clearInterval(autoSyncTimerRef.current);
      }
    };
  }, [autoSyncEnabled, autoSyncInterval, silentSyncIP]);

  // 자동 동기화 설정 저장
  const saveAutoSyncSettings = (enabled, interval) => {
    localStorage.setItem(AUTO_SYNC_STORAGE_KEY, JSON.stringify({ enabled, interval }));
  };

  const handleAutoSyncToggle = (enabled) => {
    setAutoSyncEnabled(enabled);
    saveAutoSyncSettings(enabled, autoSyncInterval);
    enqueueSnackbar(
      enabled ? `자동 IP 동기화 활성화 (${SYNC_INTERVAL_OPTIONS.find(o => o.value === autoSyncInterval)?.label})` : '자동 IP 동기화 비활성화',
      { variant: 'info' }
    );
  };

  const handleAutoSyncIntervalChange = (interval) => {
    setAutoSyncInterval(interval);
    saveAutoSyncSettings(autoSyncEnabled, interval);
  };

  const handleUpdatePortConfig = async () => {
    try {
      await apiService.updatePortForwardingConfig(newPortConfig);
      setPortConfig(newPortConfig);
      setConfigDialogOpen(false);
      enqueueSnackbar('포트 범위 설정이 업데이트되었습니다', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || '포트 범위 설정 업데이트 실패', { variant: 'error' });
    }
  };

  const handleAddRule = async () => {
    try {
      if (autoMode) {
        // 자동 할당 모드
        const response = await apiService.autoAssignPort({
          vm_name: newRule.vm_name,
          protocol: newRule.protocol,
          vm_port: parseInt(newRule.vm_port),
          description: newRule.description || null,
        });
        enqueueSnackbar(response.data.message, { variant: 'success' });
      } else {
        // 수동 할당 모드
        const response = await apiService.createPortForward(newRule.vm_name, {
          protocol: newRule.protocol,
          host_port: parseInt(newRule.host_port),
          vm_port: parseInt(newRule.vm_port),
          description: newRule.description || null,
        });
        enqueueSnackbar(response.data.message, { variant: 'success' });
      }

      setAddDialogOpen(false);
      setNewRule({
        vm_name: '',
        protocol: 'tcp',
        host_port: '',
        vm_port: '',
        description: '',
      });
      loadData();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || '포트 포워딩 규칙 추가 실패', { variant: 'error' });
    }
  };

  const handleDeleteRule = async (vmName, protocol, hostPort) => {
    if (!window.confirm(`포트 포워딩 규칙을 삭제하시겠습니까?\n${protocol}/${hostPort}`)) {
      return;
    }

    try {
      await apiService.deletePortForward(vmName, protocol, hostPort);
      enqueueSnackbar('포트 포워딩 규칙이 삭제되었습니다', { variant: 'success' });
      loadData();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || '포트 포워딩 규칙 삭제 실패', { variant: 'error' });
    }
  };

  const handleSyncIP = async () => {
    if (!window.confirm('모든 포트 포워딩 규칙의 VM IP 주소를 현재 IP로 동기화하시겠습니까?\n\nVM 네트워크 변경 후 포트포워딩이 작동하지 않을 때 사용하세요.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.syncAllPortForwardingIP();
      const { updated_count, failed_count, details } = response.data;

      // 상세 결과 메시지 생성
      let detailMessage = '';
      if (details && details.length > 0) {
        detailMessage = details
          .filter(d => d.status === 'success')
          .map(d => `  • ${d.vm_name}: ${d.old_ip} → ${d.new_ip} (${d.rules_updated}개 업데이트)`)
          .join('\n');
      }

      if (updated_count > 0) {
        enqueueSnackbar(
          `${response.data.message}\n\n${detailMessage}`,
          { variant: 'success', style: { whiteSpace: 'pre-line' } }
        );
      } else if (failed_count > 0) {
        enqueueSnackbar(`IP 동기화 중 오류 발생: ${failed_count}개 실패`, { variant: 'warning' });
      } else {
        enqueueSnackbar('동기화할 규칙이 없거나 모든 규칙이 최신 상태입니다', { variant: 'info' });
      }

      loadData();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || 'IP 동기화 실패', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // ===== 포트 포워딩 설정 가져오기 =====
  const resetImportState = () => {
    setImportText('');
    setImportFileName('');
    setImportPreview(null);
    setImportApplied(false);
    setImportOptions({ on_duplicate: 'skip', on_port_conflict: 'skip' });
  };

  const handleImportFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImportText(String(reader.result || ''));
      setImportFileName(file.name);
      setImportPreview(null);
      setImportApplied(false);
    };
    reader.onerror = () => enqueueSnackbar('파일을 읽지 못했습니다', { variant: 'error' });
    reader.readAsText(file);
    event.target.value = '';  // 같은 파일을 다시 선택할 수 있도록 초기화
  };

  // 검사(dry-run): 아무것도 변경하지 않고 행별 처리 결과만 확인
  const handleImportPreview = async (options = importOptions) => {
    if (!importText.trim()) {
      enqueueSnackbar('가져올 내용을 입력하거나 파일을 선택하세요', { variant: 'warning' });
      return;
    }

    try {
      setImportLoading(true);
      const response = await apiService.importPortForwards({
        content: importText,
        dry_run: true,
        ...options,
      });
      setImportPreview(response.data);
      setImportApplied(false);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || '가져오기 검사 실패', { variant: 'error' });
    } finally {
      setImportLoading(false);
    }
  };

  // 옵션 변경 시 미리보기가 있으면 즉시 다시 계산
  const handleImportOptionChange = (key, value) => {
    const next = { ...importOptions, [key]: value };
    setImportOptions(next);
    if (importPreview && !importApplied) {
      handleImportPreview(next);
    }
  };

  const handleImportApply = async () => {
    const summary = importPreview?.summary || {};
    const applyCount = (summary.ready || 0) + (summary.pending_ip || 0)
      + (importOptions.on_duplicate === 'overwrite' ? (summary.duplicate || 0) : 0)
      + (importOptions.on_port_conflict === 'reassign' ? (summary.port_conflict || 0) : 0);

    const skipped = Object.entries(summary)
      .filter(([key]) => ['vm_missing', 'vm_not_nat', 'invalid'].includes(key))
      .map(([key, count]) => `  • ${IMPORT_STATUS_INFO[key]?.label || key}: ${count}건`)
      .join('\n');

    const message = `${applyCount}건의 규칙을 반영합니다.`
      + (skipped ? `\n\n다음 항목은 건너뜁니다:\n${skipped}` : '')
      + '\n\n계속하시겠습니까?';

    if (!window.confirm(message)) return;

    try {
      setImportLoading(true);
      const response = await apiService.importPortForwards({
        content: importText,
        dry_run: false,
        ...importOptions,
      });
      setImportPreview(response.data);
      setImportApplied(true);

      const result = response.data.summary || {};
      const added = (result.added || 0) + (result.updated || 0);
      enqueueSnackbar(
        `가져오기 완료: ${added}건 반영, ${result.stored || 0}건 저장(IP 대기), `
        + `${result.skipped || 0}건 건너뜀${result.failed ? `, ${result.failed}건 실패` : ''}`,
        { variant: result.failed ? 'warning' : 'success' }
      );
      loadData();
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || '가져오기 실패', { variant: 'error' });
    } finally {
      setImportLoading(false);
    }
  };

  const isPortInRange = (port) => {
    return port >= portConfig.min_port && port <= portConfig.max_port;
  };

  const isPortUsed = (port) => {
    return usedPorts.includes(port);
  };

  // 정렬 토글
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // 정렬된 규칙 목록 (1차: orderBy, 2차: vm_name, 3차: host_port)
  const sortedRules = React.useMemo(() => {
    const arr = [...rules];
    arr.sort((a, b) => {
      const cmp = compareValues(a, b, orderBy);
      if (cmp !== 0) return order === 'asc' ? cmp : -cmp;
      // 보조 정렬 키
      const nameCmp = compareValues(a, b, 'vm_name');
      if (nameCmp !== 0) return nameCmp;
      return compareValues(a, b, 'host_port');
    });
    return arr;
  }, [rules, orderBy, order]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          포트 포워딩 관리
        </Typography>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setConfigDialogOpen(true)}
          >
            포트 범위 설정
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSyncIP}
            disabled={loading || rules.length === 0}
          >
            IP 동기화
          </Button>
          <Tooltip title="주기적 IP 자동 동기화 설정">
            <Button
              variant={autoSyncEnabled ? 'contained' : 'outlined'}
              color={autoSyncEnabled ? 'success' : 'inherit'}
              startIcon={<ScheduleIcon />}
              onClick={() => setAutoSyncDialogOpen(true)}
            >
              자동 동기화{autoSyncEnabled ? ' ON' : ''}
            </Button>
          </Tooltip>
          <Tooltip title="다른 서버에서 뽑은 포트 포워딩 목록을 가져옵니다">
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => {
                resetImportState();
                setImportDialogOpen(true);
              }}
            >
              가져오기
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            새로고침
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewRule({
                vm_name: '',
                protocol: 'tcp',
                host_port: '',
                vm_port: '',
                description: '',
              });
              setAutoMode(false);
              setAddDialogOpen(true);
            }}
          >
            포트 포워딩 추가
          </Button>
        </Box>
      </Box>

      {/* 포트 범위 정보 카드 */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                설정된 포트 범위
              </Typography>
              <Typography variant="h4" color="primary">
                {portConfig.min_port} - {portConfig.max_port}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                사용 중인 포트
              </Typography>
              <Typography variant="h4" color="secondary">
                {usedPorts.length}개
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                포트 포워딩 규칙
              </Typography>
              <Typography variant="h4" color="info.main">
                {rules.length}개
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderColor: autoSyncEnabled ? 'success.main' : undefined, borderStyle: 'solid', borderWidth: autoSyncEnabled ? 1 : 0 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                자동 IP 동기화
              </Typography>
              <Typography variant="h5" color={autoSyncEnabled ? 'success.main' : 'text.secondary'}>
                {autoSyncEnabled
                  ? SYNC_INTERVAL_OPTIONS.find(o => o.value === autoSyncInterval)?.label + '마다'
                  : '비활성'}
              </Typography>
              {lastSyncTime && (
                <Typography variant="caption" color="text.secondary">
                  마지막: {lastSyncTime.toLocaleTimeString()}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 포트 포워딩 규칙 테이블 */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 56 }}>#</TableCell>
              {SORTABLE_COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.numeric ? 'left' : 'left'}
                  sortDirection={orderBy === col.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleRequestSort(col.id)}
                    IconComponent={
                      orderBy === col.id
                        ? order === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon
                        : undefined
                    }
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={SORTABLE_COLUMNS.length + 2} align="center">
                  포트 포워딩 규칙이 없습니다
                </TableCell>
              </TableRow>
            ) : (
              sortedRules.map((rule, index) => (
                <TableRow
                  key={`${rule.vm_name}-${rule.protocol}-${rule.host_port}`}
                  hover
                >
                  <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                  <TableCell>{rule.vm_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={rule.protocol.toUpperCase()}
                      size="small"
                      color={rule.protocol === 'tcp' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {rule.host_port}
                      {!isPortInRange(rule.host_port) && (
                        <Tooltip title="설정된 포트 범위를 벗어났습니다">
                          <Chip label="범위 외" size="small" color="warning" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{rule.vm_ip || '-'}</TableCell>
                  <TableCell>{rule.vm_port}</TableCell>
                  <TableCell>{rule.description || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteRule(rule.vm_name, rule.protocol, rule.host_port)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 포트 범위 설정 다이얼로그 */}
      <Dialog open={configDialogOpen} onClose={() => {
        setConfigDialogOpen(false);
        setNewPortConfig(portConfig);
      }}>
        <DialogTitle>포트 범위 설정</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              VM 포트 포워딩에 사용할 수 있는 호스트 포트 범위를 설정합니다.
            </Alert>
            <TextField
              fullWidth
              label="최소 포트"
              type="number"
              value={newPortConfig.min_port}
              onChange={(e) => setNewPortConfig({ ...newPortConfig, min_port: parseInt(e.target.value) })}
              margin="normal"
              inputProps={{ min: 1, max: 65535 }}
            />
            <TextField
              fullWidth
              label="최대 포트"
              type="number"
              value={newPortConfig.max_port}
              onChange={(e) => setNewPortConfig({ ...newPortConfig, max_port: parseInt(e.target.value) })}
              margin="normal"
              inputProps={{ min: 1, max: 65535 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setConfigDialogOpen(false);
            setNewPortConfig(portConfig);
          }}>취소</Button>
          <Button onClick={handleUpdatePortConfig} variant="contained">
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 포트 포워딩 추가 다이얼로그 */}
      <Dialog open={addDialogOpen} onClose={() => {
        setAddDialogOpen(false);
        setNewRule({
          vm_name: '',
          protocol: 'tcp',
          host_port: '',
          vm_port: '',
          description: '',
        });
        setAutoMode(false);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>포트 포워딩 추가</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoMode}
                  onChange={(e) => setAutoMode(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <AutoIcon fontSize="small" />
                  자동 포트 할당
                </Box>
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              select
              label="VM"
              value={newRule.vm_name}
              onChange={(e) => setNewRule({ ...newRule, vm_name: e.target.value })}
              margin="normal"
              required
            >
              {vms.map((vm) => (
                <MenuItem key={vm.name} value={vm.name}>
                  {vm.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              select
              label="프로토콜"
              value={newRule.protocol}
              onChange={(e) => setNewRule({ ...newRule, protocol: e.target.value })}
              margin="normal"
              required
            >
              <MenuItem value="tcp">TCP</MenuItem>
              <MenuItem value="udp">UDP</MenuItem>
            </TextField>

            {!autoMode && (
              <TextField
                fullWidth
                label="호스트 포트"
                type="number"
                value={newRule.host_port}
                onChange={(e) => setNewRule({ ...newRule, host_port: e.target.value })}
                margin="normal"
                required
                helperText={
                  newRule.host_port && isPortUsed(parseInt(newRule.host_port))
                    ? '이미 사용 중인 포트입니다'
                    : newRule.host_port && !isPortInRange(parseInt(newRule.host_port))
                    ? `포트 범위: ${portConfig.min_port}-${portConfig.max_port}`
                    : `설정된 범위: ${portConfig.min_port}-${portConfig.max_port}`
                }
                error={
                  newRule.host_port &&
                  (isPortUsed(parseInt(newRule.host_port)) || !isPortInRange(parseInt(newRule.host_port)))
                }
                inputProps={{ min: portConfig.min_port, max: portConfig.max_port }}
              />
            )}

            <TextField
              fullWidth
              label="VM 포트"
              type="number"
              value={newRule.vm_port}
              onChange={(e) => setNewRule({ ...newRule, vm_port: e.target.value })}
              margin="normal"
              required
              inputProps={{ min: 1, max: 65535 }}
            />

            <TextField
              fullWidth
              label="설명 (선택사항)"
              value={newRule.description}
              onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
              margin="normal"
              placeholder="예: SSH, HTTP, Custom Service"
            />

            {autoMode && (
              <Alert severity="info" sx={{ mt: 2 }}>
                자동 모드: 설정된 범위({portConfig.min_port}-{portConfig.max_port}) 내에서 사용 가능한 포트가 자동으로
                할당됩니다.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialogOpen(false);
            setNewRule({
              vm_name: '',
              protocol: 'tcp',
              host_port: '',
              vm_port: '',
              description: '',
            });
            setAutoMode(false);
          }}>취소</Button>
          <Button
            onClick={handleAddRule}
            variant="contained"
            disabled={
              !newRule.vm_name ||
              !newRule.vm_port ||
              (!autoMode && !newRule.host_port) ||
              (!autoMode && newRule.host_port && isPortUsed(parseInt(newRule.host_port))) ||
              (!autoMode && newRule.host_port && !isPortInRange(parseInt(newRule.host_port)))
            }
          >
            추가
          </Button>
        </DialogActions>
      </Dialog>

      {/* 자동 IP 동기화 설정 다이얼로그 */}
      <Dialog open={autoSyncDialogOpen} onClose={() => setAutoSyncDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>자동 IP 동기화 설정</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              VM의 IP가 변경될 때 포트 포워딩 규칙을 자동으로 갱신합니다.
              VM 재부팅 후 IP가 바뀌는 환경에서 유용합니다.
            </Alert>
            <FormControlLabel
              control={
                <Switch
                  checked={autoSyncEnabled}
                  onChange={(e) => handleAutoSyncToggle(e.target.checked)}
                  color="success"
                />
              }
              label="자동 동기화 활성화"
              sx={{ mb: 2, display: 'block' }}
            />
            <FormControl fullWidth disabled={!autoSyncEnabled}>
              <InputLabel>동기화 주기</InputLabel>
              <Select
                value={autoSyncInterval}
                label="동기화 주기"
                onChange={(e) => handleAutoSyncIntervalChange(e.target.value)}
              >
                {SYNC_INTERVAL_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {lastSyncTime && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                마지막 자동 동기화: {lastSyncTime.toLocaleString()}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutoSyncDialogOpen(false)} variant="contained">
            확인
          </Button>
        </DialogActions>
      </Dialog>

      {/* 포트 포워딩 설정 가져오기 다이얼로그 */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>포트 포워딩 설정 가져오기</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              한 줄에 한 규칙씩, 탭 또는 콤마로 구분합니다 (열 제목 줄은 자동으로 무시).
              <Box component="code" sx={{ display: 'block', mt: 1, fontSize: 13 }}>
                VM 이름 · 프로토콜 · 호스트 포트 · VM IP · VM 포트 · 설명(선택)
              </Box>
              <Box component="code" sx={{ display: 'block', mt: 0.5, fontSize: 13, opacity: 0.8 }}>
                {IMPORT_SAMPLE}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                VM IP는 참고용으로만 읽고, 실제 규칙은 가져오는 시점의 VM IP로 만들어집니다.
              </Typography>
            </Alert>

            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                파일 선택
                <input type="file" hidden accept=".txt,.tsv,.csv,.md" onChange={handleImportFileSelect} />
              </Button>
              {importFileName && (
                <Typography variant="body2" color="text.secondary">{importFileName}</Typography>
              )}
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={5}
              maxRows={12}
              label="가져올 내용"
              value={importText}
              onChange={(e) => {
                setImportText(e.target.value);
                setImportPreview(null);
                setImportApplied(false);
              }}
              placeholder={IMPORT_SAMPLE}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />

            <Box display="flex" gap={2} mt={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 220 }} disabled={importApplied}>
                <InputLabel>이미 있는 규칙</InputLabel>
                <Select
                  value={importOptions.on_duplicate}
                  label="이미 있는 규칙"
                  onChange={(e) => handleImportOptionChange('on_duplicate', e.target.value)}
                >
                  <MenuItem value="skip">건너뛰기</MenuItem>
                  <MenuItem value="overwrite">덮어쓰기</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }} disabled={importApplied}>
                <InputLabel>호스트 포트 충돌</InputLabel>
                <Select
                  value={importOptions.on_port_conflict}
                  label="호스트 포트 충돌"
                  onChange={(e) => handleImportOptionChange('on_port_conflict', e.target.value)}
                >
                  <MenuItem value="skip">건너뛰기</MenuItem>
                  <MenuItem value="reassign">빈 포트로 자동 변경</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {importPreview && (
              <Box mt={3}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                  <Typography variant="subtitle1">
                    {importApplied ? '실행 결과' : '미리보기'} (총 {importPreview.summary?.total || 0}건)
                  </Typography>
                  {Object.entries(importPreview.summary || {})
                    .filter(([key]) => key !== 'total')
                    .map(([key, count]) => {
                      const info = (importApplied ? IMPORT_RESULT_INFO : IMPORT_STATUS_INFO)[key];
                      return (
                        <Chip
                          key={key}
                          size="small"
                          color={info?.color || 'default'}
                          label={`${info?.label || key} ${count}`}
                        />
                      );
                    })}
                </Box>

                {!importApplied && (importPreview.summary?.vm_missing > 0
                  || importPreview.summary?.vm_not_nat > 0
                  || importPreview.summary?.invalid > 0) && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    적용할 수 없는 항목이 있습니다. 아래 목록에서 확인 후 진행하면 해당 항목은 건너뜁니다.
                  </Alert>
                )}

                <TableContainer component={Paper} sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>줄</TableCell>
                        <TableCell>VM 이름</TableCell>
                        <TableCell>프로토콜</TableCell>
                        <TableCell>호스트 포트</TableCell>
                        <TableCell>VM 포트</TableCell>
                        <TableCell>설명</TableCell>
                        <TableCell>{importApplied ? '결과' : '상태'}</TableCell>
                        <TableCell>비고</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(importPreview.rows || []).map((row) => {
                        const info = importApplied
                          ? IMPORT_RESULT_INFO[row.result]
                          : IMPORT_STATUS_INFO[row.status];
                        const portChanged = row.target_host_port
                          && row.target_host_port !== row.host_port;
                        return (
                          <TableRow key={row.line} hover>
                            <TableCell sx={{ color: 'text.secondary' }}>{row.line}</TableCell>
                            <TableCell>{row.vm_name || '-'}</TableCell>
                            <TableCell>{(row.protocol || '-').toUpperCase()}</TableCell>
                            <TableCell>
                              {portChanged
                                ? `${row.host_port} → ${row.target_host_port}`
                                : (row.host_port ?? '-')}
                            </TableCell>
                            <TableCell>{row.vm_port ?? '-'}</TableCell>
                            <TableCell>{row.description || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={info?.color || 'default'}
                                label={info?.label || row.result || row.status}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                              {row.message || '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>
            {importApplied ? '닫기' : '취소'}
          </Button>
          {!importApplied && (
            <>
              <Button
                onClick={() => handleImportPreview()}
                disabled={importLoading || !importText.trim()}
                startIcon={importLoading ? <CircularProgress size={16} /> : null}
              >
                검사
              </Button>
              <Button
                variant="contained"
                onClick={handleImportApply}
                disabled={importLoading || !importPreview}
              >
                가져오기 실행
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PortForwardingPage;
